import { ChevronDown } from '@sortlist-frontend/design-system/icons';
import { useTranslation } from '@sortlist-frontend/translation/ssr';
import { formatNumber } from '@sortlist-frontend/utils';

import { rankingConfig } from '../ranking.config';
import type { RankingChart } from '../types';

interface BarChartProps {
  chart: RankingChart;
  locale: string;
}

const PROGRESS_CLASS =
  'appearance-none overflow-hidden rounded bg-secondary-100 [&::-moz-progress-bar]:bg-primary-500 [&::-webkit-progress-bar]:bg-secondary-100 [&::-webkit-progress-value]:bg-primary-500';

export const BarChart = (props: BarChartProps) => {
  const { chart, locale } = props;
  const { t } = useTranslation(rankingConfig.i18nNamespaces);
  const maxValue = Math.max(...chart.items.map((item) => item.value), 1);
  const isVertical = chart.orientation === 'vertical';

  return (
    <figure className="m-0">
      <figcaption>
        <p className="font-semibold text-secondary-950">{chart.title}</p>
        <p className="mt-1 text-sm text-secondary-500">{chart.description}</p>
      </figcaption>
      {isVertical ? (
        <ul className="mt-6 flex items-end justify-between gap-2 sm:gap-4" aria-label={chart.title}>
          {chart.items.map((item) => {
            const value = formatNumber({ value: item.value, locale });

            return (
              <li key={item.label} className="flex flex-1 flex-col items-center gap-2 text-sm">
                <span className="font-semibold text-secondary-950">{value}</span>
                <progress
                  value={item.value}
                  max={maxValue}
                  aria-label={`${item.label}: ${value} ${chart.valueLabel}`}
                  className={`${PROGRESS_CLASS} h-40 w-8 rotate-180 [writing-mode:vertical-lr] sm:w-12`}
                />
                <span className="text-center text-xs leading-tight text-secondary-700 sm:text-sm">{item.label}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <ul className="mt-4 space-y-2" aria-label={chart.title}>
          {chart.items.map((item) => {
            const value = formatNumber({ value: item.value, locale });

            return (
              <li
                key={item.label}
                className="grid grid-cols-[minmax(0,6.5rem)_1fr_2.5rem] items-center gap-3 text-sm sm:grid-cols-[minmax(0,10rem)_1fr_2.5rem]">
                <span className="text-right leading-tight text-secondary-700">{item.label}</span>
                <progress
                  value={item.value}
                  max={maxValue}
                  aria-label={`${item.label}: ${value} ${chart.valueLabel}`}
                  className={`${PROGRESS_CLASS} h-5 w-full`}
                />
                <span className="text-right font-semibold text-secondary-950">{value}</span>
              </li>
            );
          })}
        </ul>
      )}
      <details className="group mt-3 text-sm">
        <summary className="inline-flex cursor-pointer list-none items-center gap-1 font-medium text-primary-600 [&::-webkit-details-marker]:hidden">
          <ChevronDown size={16} className="transition-transform group-open:rotate-180" aria-hidden="true" />
          {t('longtail:ranking.chart.showData')}
        </summary>
        <table className="mt-3 w-full max-w-md text-left text-secondary-700">
          <thead>
            <tr>
              <th scope="col" className="py-1 pr-4 font-semibold text-secondary-950">
                {t('longtail:ranking.chart.category')}
              </th>
              <th scope="col" className="py-1 font-semibold text-secondary-950">
                {chart.valueLabel}
              </th>
            </tr>
          </thead>
          <tbody>
            {chart.items.map((item) => (
              <tr key={item.label} className="border-t border-secondary-200">
                <td className="py-1 pr-4">{item.label}</td>
                <td className="py-1">{formatNumber({ value: item.value, locale })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
};
